import React from 'react';
import clsx from 'clsx';
import { TypedMemo } from '../../../../../common/types';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the image tiles; each part uses its own slice of it */
import styles from './ImageTiles.module.scss';
/* eslint-enable css-modules/no-unused-class */

export interface PickTileOff {
  text: string;
  /** The link under the text ("Add the lyrics", "Change the date"); absent: no link. */
  action?: { label: string; onClick: () => void };
}

interface PickTileProps {
  testId: string;
  name: string;
  isOn: boolean;
  /** The live preview in the well. */
  preview: React.ReactNode;
  badge?: React.ReactNode;
  benefit?: string;
  /** A short mono line under the benefit ("3 files: 7, 3 and 1 days"). */
  files?: string;
  /** The tile is off: it stays, dim, and says why. */
  off?: PickTileOff;
  small?: boolean;
  onPick: () => void;
}

/**
 * One tile of step 3 for an image (design studio-details `.tile`): a well with the live preview,
 * the radio mark, the name, an optional badge, the benefit line. The picked tile has the violet
 * ring. An off tile keeps its place and shows why it is off; its link stays usable.
 */
const PickTileComponent: React.FC<PickTileProps> = ({
  testId,
  name,
  isOn,
  preview,
  badge,
  benefit,
  files,
  off,
  small = false,
  onPick,
}) => {
  const isOff = off !== undefined;
  return (
    <div className={clsx(styles.tile, isOn && styles.on, isOff && styles.off, small && styles.small)}>
      <button
        type="button"
        role="radio"
        aria-checked={isOn}
        aria-disabled={isOff}
        aria-label={name}
        className="stretched-link border-0 bg-transparent p-0"
        data-testid={testId}
        onClick={() => {
          if (!isOff) {
            onPick();
          }
        }}
      />
      <div className={styles.well}>
        {preview}
        {!isOff && (
          <span className={styles.radio} aria-hidden>
            {isOn ? '✓' : ''}
          </span>
        )}
        {off !== undefined && (
          <div className={styles.offNote}>
            <span>
              {off.text}
              {off.action !== undefined && (
                <>
                  <br />
                  <button type="button" className={styles.offLink} onClick={off.action.onClick}>
                    {off.action.label}
                  </button>
                </>
              )}
            </span>
          </div>
        )}
      </div>
      <div className={styles.name}>
        <b>{name}</b>
        {badge}
      </div>
      {benefit !== undefined && <span className={styles.benefit}>{benefit}</span>}
      {files !== undefined && <span className={styles.files}>{files}</span>}
    </div>
  );
};

export const PickTile = TypedMemo(PickTileComponent);
